import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMAGES } from "../lib/images";
import { scrollToId } from "../lib/site";
import { MaskedLines } from "./Reveal";
import { FloatingLeaves } from "./Decor";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-nightgreen">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Green organic farm in morning sunlight at Unique Organic Farm"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-nightgreen/75 via-nightgreen/30 to-nightgreen/85" />
      <div className="absolute inset-0 bg-gradient-to-r from-nightgreen/60 via-transparent to-transparent" />
      <FloatingLeaves />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex flex-col justify-end pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="text-goldlight text-[11px] sm:text-xs font-semibold uppercase tracking-[0.4em]">
            Unique Organic Farm
          </span>
        </motion.div>

        <h1 className="font-serif font-semibold text-cream text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-[5.2rem] tracking-tight text-balance">
          <MaskedLines
            delay={0.35}
            lines={[
              "Pure From Our Farm",
              <span key="l2">
                to Your <em className="italic font-medium text-goldlight">Home.</em>
              </span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-6 max-w-xl text-cream/85 text-base sm:text-lg font-light leading-relaxed"
        >
          Naturally grown. Carefully nurtured. Truly organic.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollToId("categories")}
            className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-nightgreen transition-all duration-500 hover:bg-goldlight hover:shadow-[0_10px_40px_rgba(200,157,84,0.4)] hover:-translate-y-0.5"
          >
            Explore Our Products
            <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-visit-btn"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-3 rounded-full border border-cream/50 px-7 py-3.5 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-cream transition-all duration-500 hover:bg-cream hover:text-nightgreen hover:-translate-y-0.5"
          >
            <MapPin className="w-4 h-4" />
            Visit Our Farm
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="scroll-indicator"
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-cream/70 hover:text-cream transition-colors"
        aria-label="Scroll down"
      >
        <span className="text-[10px] uppercase tracking-[0.35em]">Scroll</span>
        <span className="relative w-6 h-10 rounded-full border border-cream/40 flex justify-center pt-2">
          <span className="w-1 h-2 rounded-full bg-goldlight animate-scroll-dot" />
        </span>
      </motion.button>
    </section>
  );
}
